import {
  Component,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { ToastService } from '../../../core/services/toast.service';

@Component({
  selector: 'sa-toast',
  standalone: true,
  templateUrl: './sa-toast.component.html',
  styleUrl: './sa-toast.component.css',
})
export class SaToastComponent {
  readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  readonly remainingTime = signal(0);
  readonly timerStopped = signal(false);
  readonly timerPaused = signal(false);

  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private dismissTimeout: ReturnType<typeof setTimeout> | null = null;
  private activeToastId = 0;

  constructor() {
    effect(() => {
      const currentToast = this.toastService.toast();
      this.resetTimers();

      if (!currentToast) {
        return;
      }

      this.activeToastId = currentToast.id;
      this.timerStopped.set(false);
      this.timerPaused.set(false);
      this.remainingTime.set(Math.max(1, Math.ceil(currentToast.duration / 1000)));
      this.startCountdown();
      this.scheduleDismiss(currentToast.duration, currentToast.id);
    });

    this.destroyRef.onDestroy(() => this.resetTimers());
  }

  dismiss(): void {
    this.resetTimers();
    this.toastService.dismiss();
  }

  pauseTimer(): void {
    this.timerPaused.set(true);
    this.clearCountdown();
    this.clearDismissTimeout();
  }

  resumeTimer(): void {
    if (this.timerStopped()) {
      return;
    }

    this.timerPaused.set(false);
    this.startCountdown();
    this.scheduleDismiss(this.remainingTime() * 1000, this.activeToastId);
  }

  stopTimer(): void {
    this.timerStopped.set(true);
    this.timerPaused.set(false);
    this.resetTimers();
  }

  private startCountdown(): void {
    this.clearCountdown();
    this.countdownInterval = setInterval(() => {
      if (this.timerPaused() || this.timerStopped()) {
        return;
      }

      const next = this.remainingTime() - 1;
      this.remainingTime.set(Math.max(0, next));

      if (next <= 0) {
        this.dismiss();
      }
    }, 1000);
  }

  private scheduleDismiss(durationMs: number, toastId: number): void {
    this.clearDismissTimeout();
    if (durationMs <= 0) {
      this.dismiss();
      return;
    }

    this.dismissTimeout = setTimeout(() => {
      if (
        this.toastService.toast()?.id === toastId &&
        !this.timerStopped() &&
        !this.timerPaused()
      ) {
        this.dismiss();
      }
    }, durationMs);
  }

  private resetTimers(): void {
    this.clearCountdown();
    this.clearDismissTimeout();
  }

  private clearCountdown(): void {
    if (this.countdownInterval !== null) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private clearDismissTimeout(): void {
    if (this.dismissTimeout !== null) {
      clearTimeout(this.dismissTimeout);
      this.dismissTimeout = null;
    }
  }
}
